import { Moon, Sun } from 'lucide-react';
import React, { useEffect } from 'react';
import { useChatbot } from '../../../contexts/ChatbotContext';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import { useThemeSettings } from '../../../hooks/useThemeSettings';
import { FeatureRestrictionModal } from '../../subscription/FeatureRestrictionModal';

export function ThemeSettings() {
  const { chatbotPublicId } = useChatbot();
  const { theme, setTheme, initializeTheme, initialized } = useThemeSettings();
  const { tier } = useFeatureAccess();
  const [showRestrictionModal, setShowRestrictionModal] = React.useState(false);

  useEffect(() => {
    if (!initialized && chatbotPublicId) {
      initializeTheme(chatbotPublicId);
    }
  }, [initialized, initializeTheme, chatbotPublicId]);

  const handleThemeChange = async (mode: 'light' | 'dark') => {
    if (chatbotPublicId) {
      await setTheme({ mode }, chatbotPublicId);
    }
  };

  const handlePoweredByChange = async () => {
    if (!chatbotPublicId) return;

    // Si ce n'est pas un plan pro ou enterprise, montrer le modal de restriction
    if (tier !== 'professional' && tier !== 'enterprise') {
      setShowRestrictionModal(true);
      return;
    }

    // Sinon, permettre le changement
    await setTheme({ 
      showPoweredBy: !theme.showPoweredBy 
    }, chatbotPublicId);
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-300 mb-4">
        Chatbot theme
      </label>
      
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => handleThemeChange('light')}
          className={`flex items-center gap-2 px-3 py-2 rounded-md transition ${
            theme.mode === 'light'
              ? 'bg-indigo-600 text-white'
              : 'bg-white/5 text-gray-300 hover:bg-white/10'
          }`}
        >
          <Sun className="w-4 h-4" />
          <span>Light</span>
        </button>

        <button
          onClick={() => handleThemeChange('dark')}
          className={`flex items-center gap-2 px-3 py-2 rounded-md transition ${
            theme.mode === 'dark'
              ? 'bg-indigo-600 text-white'
              : 'bg-white/5 text-gray-300 hover:bg-white/10'
          }`}
        >
          <Moon className="w-4 h-4" />
          <span>Dark</span>
        </button>
      </div>

      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-300 mb-4">
          Display options
        </label>
        
        <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
          <span className="text-gray-300">Hide "Powered by Botmaker Agency"</span>
          <button
            onClick={handlePoweredByChange}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              theme.showPoweredBy !== false ? 'bg-indigo-500' : 'bg-gray-700'
            } ${(tier !== 'professional' && tier !== 'enterprise') ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                theme.showPoweredBy !== false ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      <FeatureRestrictionModal
        isOpen={showRestrictionModal}
        onClose={() => setShowRestrictionModal(false)}
        feature="Hiding the mention 'Propulsed by Botmaker Agency"
      />
    </div>
  );
}